import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon, 
  ArrowPathIcon, 
  XMarkIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import useFeedbackRetry from '../../../hooks/useFeedbackRetry';

/**
 * Notification component for pending feedback retries
 * Shows when there are failed submissions that can be retried
 */
const FeedbackRetryNotification = () => {
  const { pendingCount, isRetrying, retryError, retryFailedSubmissions } = useFeedbackRetry();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Show notification when there are pending submissions
  useEffect(() => {
    if (pendingCount > 0 && !isDismissed) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [pendingCount, isDismissed]);

  // Handle retry
  const handleRetry = async () => {
    const result = await retryFailedSubmissions();
    
    if (result.success && result.retriedCount > 0) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        if (result.remainingCount === 0) {
          setIsVisible(false);
          setIsDismissed(false); // Reset for future failures
        }
      }, 3000);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        {showSuccess ? (
          /* Success State */
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Feedback Submitted!
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Your pending feedback has been successfully submitted.
              </p>
            </div>
          </div>
        ) : (
          /* Retry State */
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Pending Feedback
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {pendingCount} feedback submission{pendingCount > 1 ? 's' : ''} failed to send. 
                Would you like to retry?
              </p>
              
              {retryError && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  Retry failed: {retryError}
                </p>
              )}
              
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 
                           text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                           focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  {isRetrying ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Retrying...</span>
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-3 h-3" />
                      <span>Retry</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleDismiss}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 
                           hover:text-gray-800 dark:hover:text-gray-200 transition-colors
                           focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                       transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              aria-label="Dismiss notification"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackRetryNotification;